import { GridApi } from '@ag-grid-community/core';
import { useCallback } from 'react';

type GridFilterApiHolder = {
    api?: GridApi | null;
};

export type GridFilterRef = GridApi | GridFilterApiHolder;

function isFilterApi(value: unknown): value is GridApi {
    return !!value && typeof value === 'object' && typeof (value as GridApi).setFilterModel === 'function';
}

function getGridApi(current: GridFilterRef | null | undefined): GridApi | undefined {
    if (!current) {
        return undefined;
    }
    // AgGridReact stores the real GridApi on `.api` (an object).
    // GridApi itself is a Proxy: every property including `.api` is a function — do not follow that.
    const nested = (current as GridFilterApiHolder).api;
    if (isFilterApi(nested)) {
        return nested;
    }
    return isFilterApi(current) ? current : undefined;
}

export function useGridFilterActions(
    gridRef: { readonly current: GridFilterRef | null },
    defaultFilterModel?: Record<string, unknown> | null,
) {
    const removeFilter = useCallback(
        (colId: string) => {
            const api = getGridApi(gridRef.current);
            if (!api) {
                return;
            }
            const next = { ...(api.getFilterModel() ?? {}) };
            delete next[colId];
            api.setFilterModel(next);
        },
        [gridRef],
    );

    const clearFilters = useCallback(() => {
        const api = getGridApi(gridRef.current);
        if (!api) {
            return;
        }
        const hasDefaults = !!defaultFilterModel && Object.keys(defaultFilterModel).length > 0;
        if (hasDefaults) {
            api.setFilterModel({ ...defaultFilterModel });
            return;
        }
        api.setFilterModel(null);
        api.applyColumnState({ state: [], defaultState: { sort: null } });
    }, [gridRef, defaultFilterModel]);

    return { removeFilter, clearFilters };
}
